import {
  EnvironmentInjector,
  Injectable,
  isDevMode,
  runInInjectionContext,
} from '@angular/core';
import {
  collection,
  collectionData,
  collectionGroup,
  CollectionReference,
  deleteDoc,
  doc,
  docData,
  DocumentData,
  DocumentReference,
  getDoc,
  limit,
  Query,
  query,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  setDoc,
  updateDoc,
} from '@angular/fire/firestore';
import { Entity } from '@models';
import { convertTimestampsToDates } from '@utils';
import { filter, forkJoin, map, Observable, takeUntil, tap } from 'rxjs';
import { AuthService } from './auth.service';
import { TenantFirebaseService } from './tenant-firebase.service';

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  private documentsRead = 0;

  constructor(
    private tenantFirebase: TenantFirebaseService,
    private authService: AuthService,
    private readonly injector: EnvironmentInjector,
  ) {}

  /** Active Firestore instance — master or enterprise tenant's project. Use for runTransaction / writeBatch. */
  get db() {
    return this.tenantFirebase.db;
  }

  getCollectionRef(path: string): CollectionReference<DocumentData> {
    return collection(this.tenantFirebase.db, path);
  }

  generateId(path: string): string {
    const col = this.getCollectionRef(path);
    return doc(col).id;
  }

  getDocumentRef<T = DocumentData>(
    path: string,
    id: string,
  ): DocumentReference<T> {
    const col = this.getCollectionRef(path);
    return doc(col, id) as DocumentReference<T>;
  }

  getDocument<T extends Entity>(
    collectionPath: string,
    id: string,
  ): Observable<T> {
    return runInInjectionContext(this.injector, () => {
      const docRef = this.getDocumentRef<T>(collectionPath, id);
      return docData<T>(docRef, { idField: 'id' }).pipe(
        tap((res) => this.countDatabaseReads(1, [docRef.path], res)),
        filter((res): res is T => {
          if (res === undefined) {
            console.warn(
              `[Firestore] Document "${id}" not yet available on "${collectionPath}", waiting...`,
            );
            return false;
          }
          return true;
        }),
        map((res: T) => {
          if (res.deleted) {
            throw new Error(
              `Document "${id}" from "${collectionPath}" is deleted.`,
            );
          }
          return convertTimestampsToDates(res);
        }),
        takeUntil(this.authService.onLogout),
      );
    });
  }

  findDocument<T extends Entity>(
    collectionPath: string,
    ...queryConstraints: QueryConstraint[]
  ): Observable<T | null> {
    return this.listDocuments<T>(
      collectionPath,
      ...queryConstraints,
      limit(1),
    ).pipe(
      map((d) => d[0] ?? null),
      takeUntil(this.authService.onLogout),
    );
  }

  getAllDocuments<T extends Entity>(
    collectionPath: string,
    ids: string[],
  ): Observable<T[]> {
    return forkJoin(ids.map((id) => this.getDocument<T>(collectionPath, id)));
  }

  listDocuments<T extends Entity>(
    path: string,
    ...queryConstraints: (QueryConstraint | QueryCompositeFilterConstraint)[]
  ): Observable<T[]> {
    return runInInjectionContext(this.injector, () => {
      const col = this.getCollectionRef(path);
      const q = query<T, DocumentData>(
        col as unknown as Query<T>,
        ...(queryConstraints as QueryConstraint[]),
      );
      return collectionData<T>(q, { idField: 'id' }).pipe(
        tap((d) => this.countDatabaseReads(d.length, [path], d)),
        map((s) =>
          s
            .filter((d) => d.deleted !== true)
            .map((el) => convertTimestampsToDates(el)),
        ),
        takeUntil(this.authService.onLogout),
      );
    });
  }

  listDocumentsGroup<T extends Entity>(
    groupId: string,
    ...queryConstraints: QueryConstraint[]
  ): Observable<T[]> {
    return runInInjectionContext(this.injector, () => {
      const q = query<T, DocumentData>(
        collectionGroup(this.tenantFirebase.db, groupId) as Query<T>,
        ...queryConstraints,
      );
      return collectionData<T>(q, { idField: 'id' }).pipe(
        tap((d) => this.countDatabaseReads(d.length, [groupId], d)),
        map((s) =>
          s
            .filter((d) => d.deleted !== true)
            .map((el) => convertTimestampsToDates(el)),
        ),
        takeUntil(this.authService.onLogout),
      );
    });
  }

  exists(collectionPath: string, id: string): Promise<boolean> {
    return runInInjectionContext(this.injector, () => {
      const docRef = this.getDocumentRef(collectionPath, id);
      this.countDatabaseReads(1, ['exists', id]);
      return getDoc(docRef)
        .then((d) => d.exists())
        .catch(() => false);
    });
  }

  async create<T>(
    collectionPath: string,
    data: Partial<T>,
    id?: string,
  ): Promise<string> {
    const dbData: any = {
      ...data,
      id: id ?? this.generateId(collectionPath),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    const col = this.getCollectionRef(collectionPath);
    const docRef = doc(col, dbData.id);
    await setDoc<DocumentData, DocumentData>(docRef, dbData);
    return dbData.id;
  }

  async createOrUpdate<T extends Entity>(
    collectionPath: string,
    id: string,
    data: Partial<T>,
  ): Promise<void> {
    if (data.id && data.id !== id) {
      throw new Error('Document id mismatch');
    }
    const dbData: Partial<T> = { ...data, id } as Partial<T>;
    const exists = await this.exists(collectionPath, id);
    if (!exists) {
      (dbData as any).createdAt = new Date();
    }
    (dbData as any).updatedAt = new Date();
    const docRef = this.getDocumentRef(collectionPath, id);
    return setDoc<DocumentData, DocumentData>(docRef as any, dbData as any, {
      merge: true,
    });
  }

  async update<T extends Entity>(
    collectionPath: string,
    data: Partial<T>,
  ): Promise<void> {
    const id = data.id;
    if (!id) throw new Error('Document data does not contain ID');
    const dbData: Partial<T> = { ...data, updatedAt: new Date() as any };
    const docRef = this.getDocumentRef(collectionPath, id);
    return updateDoc<DocumentData, DocumentData>(docRef as any, dbData as any);
  }

  /**
   * Updates only the given fields (plus `updatedAt`) WITHOUT writing the `id`
   * field. `update()` spreads `id` into the payload, which appears as an *added*
   * key on docs created via `.add()` (no stored `id`) — breaking Firestore
   * rules that restrict the mutable key set (e.g. an owner flipping a
   * notification's `read` flag: `affectedKeys().hasOnly(['read','updatedAt'])`).
   */
  async updateFields<T extends Entity>(
    collectionPath: string,
    id: string,
    fields: Partial<Omit<T, 'id'>>,
  ): Promise<void> {
    const dbData: any = { ...fields, updatedAt: new Date() };
    const docRef = this.getDocumentRef(collectionPath, id);
    return updateDoc<DocumentData, DocumentData>(docRef as any, dbData as any);
  }

  async delete(collectionPath: string, id: string): Promise<void> {
    return this.update(collectionPath, { id, deleted: true });
  }

  async deleteForever(collectionPath: string, id: string): Promise<void> {
    const docRef = this.getDocumentRef(collectionPath, id);
    return deleteDoc(docRef as any);
  }

  private countDatabaseReads(
    length: number,
    query: unknown[] = [],
    data?: unknown,
  ) {
    const newCount = this.documentsRead + length;
    if (isDevMode()) {
      console.debug(
        '%c[FirestoreService]',
        'background: #F9CB36; color: #000',
        `Reads: ${this.documentsRead} (+${length}) => ${newCount}`,
        query,
        data,
      );
    }
    this.documentsRead = newCount;
  }
}
